'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { PageState } from '@/components/common/feedback/PageState';
import {
  Plus,
  RefreshCw,
  Search,
  Filter,
  Ticket,
  Clock,
  ChevronRight,
  ChevronLeft,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  getTickets,
  getCategories,
  TicketListItem,
  TicketCategory,
  TicketStatus,
  TicketPriority,
  QueryTicketParams,
  getStatusLabel,
  getStatusColor,
  getPriorityLabel,
  getPriorityColor,
  ApiClientError,
} from '@/services/api/tickets';

/**
 * 工单列表页面
 * 遵循飞书风格设计规范
 */
export default function TicketListPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation();

  // 状态
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  // 筛选
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>((searchParams.get('status') as TicketStatus) || '');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // 加载分类
  useEffect(() => {
    getCategories()
      .then((data) => setCategories(data || []))
      .catch(() => setCategories([]));
  }, []);

  // 加载工单列表
  const loadTickets = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const params: QueryTicketParams = {
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      };

      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (categoryFilter) params.categoryId = categoryFilter;
      if (keyword) params.keyword = keyword;

      const result = await getTickets(params);
      setTickets(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.tickets?.error?.loadFailed || '加载失败');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, limit, statusFilter, priorityFilter, categoryFilter, keyword, t]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // 搜索
  const handleSearch = () => {
    setKeyword(searchInput);
    setPage(1);
  };

  // 重置筛选
  const handleResetFilters = () => {
    setStatusFilter('');
    setPriorityFilter('');
    setCategoryFilter('');
    setKeyword('');
    setSearchInput('');
    setPage(1);
  };

  // 刷新
  const handleRefresh = () => {
    loadTickets(true);
  };

  // 格式化时间
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (hours < 1) return t.common?.justNow || '刚刚';
    if (hours < 24) return `${hours}${t.common?.hoursAgo || '小时前'}`;
    if (days < 7) return `${days}${t.common?.daysAgo || '天前'}`;
    
    return date.toLocaleDateString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 是否有筛选条件
  const hasFilters = statusFilter || priorityFilter || categoryFilter || keyword;

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: '#3370ff' }} />
          <p className="mt-4 text-sm" style={{ color: '#8f959e' }}>
            {t.common?.loading || '加载中...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* 标题栏 - 飞书风格，标准 h-12 高度 */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
            {/* 左侧：搜索 + 筛选 */}
            <div className="flex items-center gap-3">
              {/* 搜索框 */}
              <div className="relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={t.tickets?.search?.placeholder || '搜索工单标题、编号...'}
                  className="px-3 py-1.5 pl-9 rounded-lg text-sm outline-none transition-all"
                  style={{ 
                    width: '280px',
                    backgroundColor: '#f7f8fa',
                    color: '#1f2329',
                    border: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.backgroundColor = '#eff0f2';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.backgroundColor = '#f7f8fa';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <Search 
                  className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" 
                  style={{ color: '#8f959e' }} 
                />
              </div>

              {/* 状态筛选 */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as TicketStatus | '');
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-lg text-sm outline-none transition-all cursor-pointer"
                style={{ 
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329',
                  border: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.backgroundColor = '#eff0f2';
                }}
                onBlur={(e) => {
                  e.target.style.backgroundColor = '#f7f8fa';
                }}
              >
                <option value="">{t.tickets?.filter?.allStatus || '全部状态'}</option>
                {Object.values(TicketStatus).map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status, t)}
                  </option>
                ))}
              </select>

              {/* 优先级筛选 */}
              <select
                value={priorityFilter}
                onChange={(e) => {
                  setPriorityFilter(e.target.value as TicketPriority | '');
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-lg text-sm outline-none transition-all cursor-pointer"
                style={{ 
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329',
                  border: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.backgroundColor = '#eff0f2';
                }}
                onBlur={(e) => {
                  e.target.style.backgroundColor = '#f7f8fa';
                }}
              >
                <option value="">{t.tickets?.filter?.allPriority || '全部优先级'}</option>
                {Object.values(TicketPriority).map((priority) => (
                  <option key={priority} value={priority}>
                    {getPriorityLabel(priority, t)}
                  </option>
                ))}
              </select>

              {/* 分类筛选 */}
              {categories.length > 0 && (
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm outline-none transition-all cursor-pointer"
                  style={{ 
                    backgroundColor: '#f7f8fa',
                    color: '#1f2329',
                    border: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.backgroundColor = '#eff0f2';
                  }}
                  onBlur={(e) => {
                    e.target.style.backgroundColor = '#f7f8fa';
                  }}
                >
                  <option value="">{t.tickets?.filter?.allCategory || '全部分类'}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              )}

              {/* 重置筛选 */}
              {hasFilters && (
                <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-all"
                  style={{ 
                    backgroundColor: '#f7f8fa',
                    color: '#646a73'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#eff0f2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f7f8fa';
                  }}
                >
                  <X className="w-4 h-4" />
                  {t.common?.reset || '重置'}
                </button>
              )}
            </div>

            {/* 右侧：操作按钮 */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329'
                }}
                onMouseEnter={(e) => {
                  if (!refreshing) e.currentTarget.style.backgroundColor = '#eff0f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7f8fa';
                }}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {t.common?.refresh || '刷新'}
              </button>

              <button
                onClick={() => router.push('/tickets/new')}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: '#3370ff',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1e5eff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3370ff';
                }}
              >
                <Plus className="w-4 h-4" />
                {t.tickets?.create || '新建工单'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 内容区域 - flex-1 自动占满剩余空间 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 统计信息 */}
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm" style={{ color: '#8f959e' }}>
              {t.tickets?.list?.total?.replace('{total}', String(total)) || `共 ${total} 个工单`}
            </div>
          </div>

          {/* 工单列表卡片 */}
          <div className="bg-white rounded-lg" style={{ border: '1px solid #e5e6eb' }}>
            {tickets.length === 0 ? (
              <div className="p-12">
                <PageState
                  variant="empty"
                  title={t.tickets.empty}
                  description={hasFilters ? t.tickets.noFilterResult : t.tickets.emptyDesc}
                  actionLabel={!hasFilters ? t.tickets.create : undefined}
                  onAction={!hasFilters ? () => router.push('/tickets/new') : undefined}
                  layout="center"
                  className="mx-auto w-full max-w-2xl border-0 bg-transparent"
                />
              </div>
            ) : (
              <>
                {/* 表头 */}
                <div 
                  className="px-6 py-3 border-b grid grid-cols-12 gap-4" 
                  style={{ 
                    borderColor: '#e5e6eb', 
                    backgroundColor: '#fafafa' 
                  }}
                >
                  <div className="col-span-1 text-xs font-medium" style={{ color: '#8f959e' }}>
                    {t.tickets?.field?.priority || '优先级'}
                  </div>
                  <div className="col-span-5 text-xs font-medium" style={{ color: '#8f959e' }}>
                    {t.tickets?.field?.title || '标题'}
                  </div>
                  <div className="col-span-2 text-xs font-medium" style={{ color: '#8f959e' }}>
                    {t.tickets?.field?.status || '状态'}
                  </div>
                  <div className="col-span-2 text-xs font-medium" style={{ color: '#8f959e' }}>
                    {t.tickets?.field?.assignee || '处理人'}
                  </div>
                  <div className="col-span-2 text-xs font-medium" style={{ color: '#8f959e' }}>
                    {t.tickets?.field?.createdAt || '创建时间'}
                  </div>
                </div>

                {/* 列表项 */}
                {tickets.map((ticket, index) => {
                  const statusColor = getStatusColor(ticket.status);
                  const priorityColor = getPriorityColor(ticket.priority);

                  return (
                    <div
                      key={ticket.id}
                      onClick={() => router.push(`/tickets/${ticket.id}`)}
                      className="px-6 py-4 border-b transition-colors cursor-pointer grid grid-cols-12 gap-4 items-center"
                      style={{ 
                        borderColor: index === tickets.length - 1 ? 'transparent' : '#e5e6eb'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fafafa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                    >
                      {/* 优先级 */}
                      <div className="col-span-1">
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: priorityColor.bg.includes('red') ? 'rgba(245, 63, 63, 0.1)' :
                              priorityColor.bg.includes('orange') ? 'rgba(255, 125, 0, 0.1)' :
                              priorityColor.bg.includes('blue') ? 'rgba(51, 112, 255, 0.1)' : 'rgba(134, 144, 156, 0.1)',
                            color: priorityColor.text.includes('red') ? '#f53f3f' :
                              priorityColor.text.includes('orange') ? '#ff7d00' :
                              priorityColor.text.includes('blue') ? '#3370ff' : '#86909c'
                          }}
                        >
                          {getPriorityLabel(ticket.priority, t)}
                        </span>
                      </div>

                      {/* 标题 */}
                      <div className="col-span-5 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-mono flex-shrink-0" style={{ color: '#8f959e' }}>
                            {ticket.ticketNo}
                          </span>
                          <h3 className="text-sm font-medium truncate" style={{ color: '#1f2329' }}>
                            {ticket.title}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          {ticket.category && (
                            <span className="text-xs" style={{ color: '#8f959e' }}>
                              {ticket.category.name}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* 状态 */}
                      <div className="col-span-2">
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: statusColor.bg.includes('blue') ? 'rgba(51, 112, 255, 0.1)' :
                              statusColor.bg.includes('yellow') ? 'rgba(255, 125, 0, 0.1)' :
                              statusColor.bg.includes('purple') ? 'rgba(151, 117, 250, 0.1)' :
                              statusColor.bg.includes('green') ? 'rgba(0, 180, 42, 0.1)' : 'rgba(134, 144, 156, 0.1)',
                            color: statusColor.text.includes('blue') ? '#3370ff' :
                              statusColor.text.includes('yellow') ? '#ff7d00' :
                              statusColor.text.includes('purple') ? '#9775fa' :
                              statusColor.text.includes('green') ? '#00b42a' : '#86909c'
                          }}
                        >
                          {getStatusLabel(ticket.status, t)}
                        </span>
                      </div>

                      {/* 处理人 */}
                      <div className="col-span-2">
                        {ticket.assignee ? (
                          <span className="text-sm" style={{ color: '#1f2329' }}>
                            {ticket.assignee.displayName}
                          </span>
                        ) : (
                          <span className="text-sm" style={{ color: '#8f959e' }}>
                            {t.common?.unassigned || '未分配'}
                          </span>
                        )}
                      </div>

                      {/* 创建时间 */}
                      <div className="col-span-2 flex items-center justify-between">
                        <div className="flex items-center gap-1 text-xs" style={{ color: '#8f959e' }}>
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(ticket.createdAt)}
                        </div>
                        <ChevronRight className="w-4 h-4" style={{ color: '#c9cdd4' }} />
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* 分页 */}
            {totalPages > 1 && (
              <div 
                className="px-6 py-4 border-t flex items-center justify-between" 
                style={{ borderColor: '#e5e6eb' }}
              >
                <div className="text-sm" style={{ color: '#8f959e' }}>
                  显示 {(page - 1) * limit + 1}-{Math.min(page * limit, total)} / {total}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="px-3 py-1 rounded text-sm flex items-center gap-1"
                    style={{ 
                      backgroundColor: page === 1 ? '#f7f8fa' : '#ffffff',
                      color: page === 1 ? '#c9cdd4' : '#1f2329',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      border: '1px solid #e5e6eb'
                    }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t.common?.previousPage || '上一页'}
                  </button>
                  
                  <span className="text-sm" style={{ color: '#646a73' }}>
                    {page} / {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="px-3 py-1 rounded text-sm flex items-center gap-1"
                    style={{ 
                      backgroundColor: page === totalPages ? '#f7f8fa' : '#ffffff',
                      color: page === totalPages ? '#c9cdd4' : '#1f2329',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      border: '1px solid #e5e6eb'
                    }}
                  >
                    {t.common?.nextPage || '下一页'}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
